import React, { useMemo } from 'react';
import { Col, Spin, Typography, Alert, List, Tag, Button, Space } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { buildCommandCenterPortfolio } from '@/components/epc/commercial-ux/commandCenterPortfolio';
import { EpcCommercialIntelligenceCompact } from '@/components/epc/commercial-ux/EpcCommercialIntelligenceCompact';

const { Title, Paragraph, Text } = Typography;

const EpcCommercialControlTowerPage: React.FC = () => {
  const navigate = useNavigate();
  const dashQ = useQuery({
    queryKey: ['epc-commercial-dashboard', 'control-tower'],
    queryFn: () => epcCommercialService.getDashboard(),
    staleTime: 30_000,
  });

  const portfolio = useMemo(() => buildCommandCenterPortfolio(dashQ.data), [dashQ.data]);
  const m = portfolio.totals;

  if (dashQ.isLoading && !dashQ.data) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        EPC Commercial Control Tower
      </Title>
      <Paragraph type="secondary">
        Pipeline health across tender → mobilization. Same portfolio model as Command Center.
      </Paragraph>

      {dashQ.isError ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={(dashQ.error as Error)?.message || 'Dashboard unavailable'}
        />
      ) : null}

      <ControlTowerSectionCard zone="attention" title="Needs attention" style={{ marginBottom: 16 }}>
        {!portfolio.attention.length ? (
          <Alert type="success" showIcon message="No attention items" />
        ) : (
          <List
            size="small"
            dataSource={portfolio.attention}
            renderItem={(a) => (
              <List.Item
                actions={[
                  <Button key="go" type="link" onClick={() => navigate(a.href)}>
                    Open
                  </Button>,
                ]}
              >
                <Space wrap>
                  <Tag
                    color={
                      a.severity === 'critical'
                        ? 'error'
                        : a.severity === 'warning'
                          ? 'warning'
                          : 'default'
                    }
                  >
                    {a.severity}
                  </Tag>
                  <Text>{a.title}</Text>
                </Space>
              </List.Item>
            )}
          />
        )}
      </ControlTowerSectionCard>

      <ControlTowerSectionCard zone="attention" title="Commercial pipeline" style={{ marginBottom: 16 }}>
        <ControlTowerMetricStrip zone="attention" title="Open work">
          {portfolio.kpis.map((k) => (
            <Col xs={12} sm={8} md={6} lg={4} key={k.id}>
              <ControlTowerMetricTile
                title={k.label}
                count={k.count}
                urgency={
                  k.urgency === 'critical'
                    ? 'critical'
                    : k.urgency === 'attention'
                      ? 'attention'
                      : 'neutral'
                }
                footerLink={{
                  label: 'Open',
                  onClick: () => navigate(k.href),
                }}
              />
            </Col>
          ))}
        </ControlTowerMetricStrip>
      </ControlTowerSectionCard>

      <ControlTowerSectionCard zone="neutral" title="Totals" style={{ marginBottom: 16 }}>
        <ControlTowerMetricStrip zone="neutral" title="Register counts">
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile title="All tenders" count={m.tenders} />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile title="All awards" count={m.awards} />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile title="All contracts" count={m.contracts} />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile title="All mobilizations" count={m.mobilizations} />
          </Col>
        </ControlTowerMetricStrip>
      </ControlTowerSectionCard>

      <EpcCommercialIntelligenceCompact title="EOIP / Memory (advisory)" />
    </div>
  );
};

export default EpcCommercialControlTowerPage;
